import { Injectable } from '@angular/core';
import { forkJoin, Observable, lastValueFrom } from 'rxjs';
import { WebService } from './web.service';

type OrderRefKey = 'OrderStatuses' | 'PaymentMethods';

type OrderRefOptions = {
  keys: 'ALL' | OrderRefKey[];
  exclude?: OrderRefKey[];
};

@Injectable({
  providedIn: 'root'
})
export class OrdersDataService {
  constructor(private web: WebService) { }

  // ---------------------------------------------------------------------
  // Order-specific reference data (mirrors InventoryDataService's pattern)
  // ---------------------------------------------------------------------
  private refSources: Record<OrderRefKey, () => Observable<any>> = {
    OrderStatuses: () => this.web.getWebCall('data', 'coffee/order-statuses'),
    PaymentMethods: () => this.web.getWebCall('data', 'coffee/payment-methods'),
  };

  getRefData(options: OrderRefOptions): Promise<any> {
    const { keys, exclude = [] } = options;

    const resolvedKeys: OrderRefKey[] =
      keys === 'ALL'
        ? (Object.keys(this.refSources) as OrderRefKey[]).filter(k => !exclude.includes(k))
        : keys;

    const observables = resolvedKeys.reduce((acc, key) => {
      acc[key] = this.refSources[key]();
      return acc;
    }, {} as Record<string, Observable<any>>);

    return lastValueFrom(forkJoin(observables));
  }

  // ---------------------------------------------------------------------
  // Helper: current user's GraphUserID, per the app-wide identity pattern
  // ---------------------------------------------------------------------
  private get actionByGUID(): string | null {
    return localStorage.getItem('GUID');
  }

  // ---------------------------------------------------------------------
  // Order CRUD / lifecycle
  // ---------------------------------------------------------------------

  /** List orders (Order Management screen). Pass CompanyID to scope to one customer. */
  getSalesOrders(params?: { OrderID?: number; CompanyID?: number; summary?: boolean; recentOnly?: boolean }): Observable<any> {
    const httpParams = params
      ? Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([name, value]) => ({ name, value }))
      : undefined;

    return this.web.getWebCall('orders', 'coffee/sale-orders/', { params: httpParams, skipLoader: params?.summary === true });
  }

  /** Single order — header, line items, and action history. */
  getOrderDetails(orderId: number): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sale-order', {
      params: [{ name: 'id', value: orderId }]
    });
  }

  /** "Need Approval" queue (Order Management screen tab). */
  getOrdersPendingApproval(): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/pending-approval');
  }

  /** Order action history only (used outside the full detail view, e.g. a history panel). */
  getOrderHistory(orderId: number): Observable<any> {
    return this.web.getWebCall('orders', `coffee/sales-order/${orderId}/history`);
  }

  createOrder(payload: any): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/create', {
      body: { ...payload, ActionByGUID: this.actionByGUID }
    });
  }

  updateOrder(payload: any): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/update', {
      body: { ...payload, ActionByGUID: this.actionByGUID }
    });
  }

  createOrderDetail(payload: any): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/detail/create', {
      body: { ...payload, ActionByGUID: this.actionByGUID }
    });
  }

  updateOrderDetail(payload: any): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/detail/update', {
      body: { ...payload, ActionByGUID: this.actionByGUID }
    });
  }

  resolvePriceException(orderPriceExceptionId: number, approve: boolean): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/price-exception/resolve', {
      body: {
        OrderPriceExceptionID: orderPriceExceptionId,
        Approve: approve,
        ApprovedByGUID: this.actionByGUID
      }
    });
  }

  sendToOutbound(orderId: number): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/send-to-outbound', {
      body: { OrderID: orderId, ActionByGUID: this.actionByGUID }
    });
  }

  cancelOrder(orderId: number, reason?: string): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/cancel', {
      body: { OrderID: orderId, Reason: reason, ActionByGUID: this.actionByGUID }
    });
  }

  saveShippingAddresses(orderId: number, addresses: any[]): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/shipping-addresses/save', {
      body: { OrderID: orderId, Addresses: addresses }
    });
  }
  mergeOrder(payload: any): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/merge', {
      body: { ...payload, ActionByGUID: this.actionByGUID }
    });
  }

  getOrderPayments(orderId: number): Observable<any> {
    return this.web.getWebCall('orders', `coffee/sales-order/${orderId}/payments`);
  }

  saveOrderPayment(payload: any): Observable<any> {
    return this.web.getWebCall('orders', 'coffee/sales-order/payments', {
      body: { ...payload, ActionBy: this.actionByGUID }
    });
  }


  uploadPaymentReceipt(orderPaymentId: number, orderId: number, base64Pdf: string): Observable<any> {
    return this.web.getWebCall('orders', `coffee/sales-order/payments/${orderPaymentId}/receipt`, {
      body: { OrderID: orderId, Base64Pdf: base64Pdf }
    });
  }


  getMyOrdersStatus(): Observable<any> {
    const guid = localStorage.getItem('GUID');
    return this.web.getWebCall('orders', 'coffee/my-orders-status', {
      params: [{ name: 'UserGUID', value: guid }]
    });
  }

}
